import { safeGet } from '../../../utils/safeGet'
import { post as _post } from '../../../utils/fetcher'
import { INIT_DEFINITION_SETTINGS, SCORING_DEFINITION_TYPES } from '../../../settings/scoringProfilesSettings'
import { isNearlyEqual } from '../../../utils/formatter'

export default {
  namespaced: true,

  state: {
    loading: false,
    isDuplicate: false,
    profileCoverage: {},
    scoringProfileId: null,
    scoringProfileName: '',
    defaultScoringProfileName: '',
    scoringProfileDescription: '',
    defaultScoringProfileDescription: '',
    isProfileCoverageProvidedByAPI: false,
  },

  mutations: {
    setLoading(state, data) {
      state.loading = data
    },

    setIsDuplicate(state, payload) {
      state.isDuplicate = payload
    },

    setProfileCoverage(state, payload) {
      state.profileCoverage = payload
    },

    setIsProfileCoverageProvidedByAPI(state, payload) {
      state.isProfileCoverageProvidedByAPI = payload
    },

    setScoringProfileId(state, payload) {
      state.scoringProfileId = payload
    },

    setScoringProfileName(state, payload) {
      state.scoringProfileName = payload
    },

    setDefaultScoringProfileName(state, payload) {
      state.defaultScoringProfileName = payload
    },

    setDefaultScoringProfileDescription(state, payload) {
      state.defaultScoringProfileDescription = payload
    },

    setScoringProfileDescription(state, payload) {
      state.scoringProfileDescription = payload
    },
  },

  getters: {
    getLoading: (state) => {
      return safeGet(() => state.loading, false)
    },

    getIsDuplicate: (state) => {
      return safeGet(() => state.isDuplicate, false)
    },

    getProfileCoverage: (state) => {
      return safeGet(() => state.profileCoverage, {})
    },

    getIsProfileCoverageProvidedByAPI: (state) => {
      return safeGet(() => state.isProfileCoverageProvidedByAPI, false)
    },

    getScoringProfileId: (state) => {
      return safeGet(() => state.scoringProfileId, null)
    },

    getScoringProfileName: (state) => {
      return safeGet(() => state.scoringProfileName, '')
    },

    getScoringProfileDescription: (state) => {
      return safeGet(() => state.scoringProfileDescription, '')
    },

    getScoringProfileNameValidation: (state, getters) => {
      const valueName = getters.getScoringProfileName
      const isValidName = typeof valueName === 'string' && valueName.trim() != ''
      return safeGet(() => isValidName, false)
    },

    getScoringProfileDefinitionValidation: (state, getters, rootState, rootGetters) => {
      const scoringDefinition = rootGetters['scoringProfilesDefinition/getSelectedScoringDefinition']
      const isValidScoringDefinition = scoringDefinition.list.length > 0

      // scoring profile definition weight: max total drivers's weights
      const selectedScoringTypeTotalWeight = rootGetters['scoringProfilesDefinition/getMaxTotalWeight']
      const epsilon = scoringDefinition.config === SCORING_DEFINITION_TYPES.custom ? 0.0001 : 0.0005
      const isValidScoringDefinitionTotalWeight = isNearlyEqual(selectedScoringTypeTotalWeight, 1, epsilon)
      // scoring profile definition weight: check if custom drivers has 0 value
      const isDriversWeightsGreaterThanZero = rootGetters['scoringProfilesDefinition/getDriversWeightsGreaterThanZero']

      return {
        isValidScoringDefinition: isValidScoringDefinition,
        isValidScoringDefinitionTotalWeight: isValidScoringDefinitionTotalWeight,
        isDriversWeightsGreaterThanZero: isValidScoringDefinition && isDriversWeightsGreaterThanZero,
      }
    },
  },

  actions: {
    setLoading({ commit }, value) {
      commit('setLoading', value)
    },

    setIsDuplicate({ commit }, value) {
      commit('setIsDuplicate', value)
    },

    setScoringProfileId({ commit }, payload) {
      commit('setScoringProfileId', payload)
    },

    setScoringProfileName({ commit }, payload) {
      commit('setScoringProfileName', payload)
    },

    setScoringProfileDescription({ commit }, payload) {
      commit('setScoringProfileDescription', payload)
    },

    setScoringProfileToDefault({ commit, dispatch }) {
      commit('setIsDuplicate', false)
      commit('setScoringProfileId', null)
      commit('setScoringProfileName', '')
      commit('setDefaultScoringProfileName', '')
      commit('setScoringProfileDescription', '')
      commit('setDefaultScoringProfileDescription', '')
      dispatch('scoringProfilesCoverageDetail/resetCoverageFilters', null, { root: true })
    },

    newScoringProfile({ commit, state, rootGetters, dispatch }) {
      dispatch('setScoringProfileToDefault')
      dispatch('scoringProfilesDefinition/setSelectedWeightType', 1, { root: true })
      dispatch('scoringProfilesDefinition/setSequenceWeightsDecay', 0.5, { root: true })
      dispatch('scoringProfilesDefinition/setScoringDefinitionSettings', INIT_DEFINITION_SETTINGS(), {
        root: true,
      })
      dispatch('scoringProfilesDefinition/setDefaultScoringDefinitionSettings', INIT_DEFINITION_SETTINGS(), {
        root: true,
      })
      dispatch(
        'scoringProfilesCoverageDetail/setUpProductsList',
        { excludedProducts: [], productsToSave: [] },
        { root: true }
      )
      dispatch('scoringProfilesDefinition/resetSelectedModel', null, { root: true })
      if (rootGetters.can_ViewScoringProfileModels) {
        dispatch('scoringProfilesDefinition/setScoringProfileDefinitionSelectValue', -1, { root: true })
      } else {
        dispatch('scoringProfilesDefinition/setScoringProfileDefinitionSelectValue', 0, { root: true })
      }
    },

    loadCommonScoringProfilesList() {
      return new Promise((resolve, reject) => {
        const payload = {
          Sf: '',
          Page: 1,
          Size: 10000,
          Sort: 'Name',
        }
        _post('/api/scoringProfiles/list', payload)
          .then((data) => {
            resolve(data)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },

    clearAllStoreModules({ dispatch }) {
      dispatch('timeHorizonIntervals/resetTimeHorizonState', null, { root: true })
      dispatch('scoringProfiles/resetState', null, { root: true })
      dispatch('productDetails/resetState', null, { root: true })
      dispatch('scoringProfilesDetails/resetState', null, { root: true })
      dispatch('scoringProfilesList/resetState', null, { root: true })
      dispatch('scoringProfilesModelsList/resetState', null, { root: true })
      dispatch('monitoringList/resetState', null, { root: true })
    },
  },
}
